import { useCallback, useRef, useState } from 'react';
import type {
  PointerEvent as ReactPointerEvent,
  TouchEvent as ReactTouchEvent,
  TouchList as ReactTouchList,
  WheelEvent as ReactWheelEvent,
} from 'react';

interface ImageZoomState {
  scale: number;
  translateX: number;
  translateY: number;
}

interface UseImageZoomOptions {
  minScale?: number;
  maxScale?: number;
  doubleTapScale?: number;
}

interface UseImageZoomReturn {
  state: ImageZoomState;
  resetZoom: () => void;
  handleWheel: (event: ReactWheelEvent) => void;
  handlePointerDown: (event: ReactPointerEvent) => void;
  handlePointerMove: (event: ReactPointerEvent) => void;
  handlePointerUp: (event: ReactPointerEvent) => void;
  handleDoubleClick: () => void;
  handleTouchStart: (event: ReactTouchEvent) => void;
  handleNativeTouchMove: (event: TouchEvent) => void;
  handleTouchEnd: () => void;
  isZoomed: boolean;
}

const DEFAULT_MIN_SCALE = 0.5;
const DEFAULT_MAX_SCALE = 5;
const DEFAULT_DOUBLE_TAP_SCALE = 2;
const ZOOM_STEP = 0.25;

const initialState: ImageZoomState = {
  scale: 1,
  translateX: 0,
  translateY: 0,
};

function getTouchDistance(touches: ReactTouchList | TouchList): number {
  const touch1 = touches[0];
  const touch2 = touches[1];
  if (!touch1 || !touch2) {
    return 0;
  }
  return Math.hypot(
    touch2.clientX - touch1.clientX,
    touch2.clientY - touch1.clientY
  );
}

export function useImageZoom(
  options: UseImageZoomOptions = {}
): UseImageZoomReturn {
  const {
    minScale = DEFAULT_MIN_SCALE,
    maxScale = DEFAULT_MAX_SCALE,
    doubleTapScale = DEFAULT_DOUBLE_TAP_SCALE,
  } = options;

  const [state, setState] = useState<ImageZoomState>(initialState);
  const isPanningRef = useRef(false);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const lastPinchDistanceRef = useRef(0);

  const clampScale = useCallback(
    (scale: number) => Math.min(maxScale, Math.max(minScale, scale)),
    [minScale, maxScale]
  );

  const resetZoom = useCallback(() => {
    setState(initialState);
  }, []);

  const handleWheel = useCallback(
    (event: ReactWheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setState((previous) => {
        const newScale = clampScale(previous.scale + delta);
        if (newScale <= 1) {
          return initialState;
        }
        return { ...previous, scale: newScale };
      });
    },
    [clampScale]
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (state.scale <= 1) {
        return;
      }
      // Capture the pointer so subsequent move/up events fire on this element
      // even when the cursor leaves the container mid-drag.
      event.currentTarget.setPointerCapture(event.pointerId);
      isPanningRef.current = true;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
    },
    [state.scale]
  );

  const handlePointerMove = useCallback((event: ReactPointerEvent) => {
    if (!isPanningRef.current) {
      return;
    }
    const deltaX = event.clientX - lastPointerRef.current.x;
    const deltaY = event.clientY - lastPointerRef.current.y;
    if (deltaX === 0 && deltaY === 0) {
      return;
    }
    lastPointerRef.current = { x: event.clientX, y: event.clientY };

    setState((previous) => ({
      ...previous,
      translateX: previous.translateX + deltaX,
      translateY: previous.translateY + deltaY,
    }));
  }, []);

  const handlePointerUp = useCallback((event: ReactPointerEvent) => {
    isPanningRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleDoubleClick = useCallback(() => {
    setState((previous) => {
      if (previous.scale > 1) {
        return initialState;
      }
      return { ...previous, scale: doubleTapScale };
    });
  }, [doubleTapScale]);

  const handleTouchStart = useCallback((event: ReactTouchEvent) => {
    if (event.touches.length === 2) {
      lastPinchDistanceRef.current = getTouchDistance(event.touches);
    }
  }, []);

  // Native listener so preventDefault works — React synthesizes touchmove as
  // a passive listener and silently ignores preventDefault, breaking pinch zoom.
  const handleNativeTouchMove = useCallback(
    (event: TouchEvent) => {
      if (event.touches.length === 2) {
        event.preventDefault();
        const currentDistance = getTouchDistance(event.touches);
        if (lastPinchDistanceRef.current > 0) {
          const scaleFactor = currentDistance / lastPinchDistanceRef.current;
          setState((previous) => {
            const newScale = clampScale(previous.scale * scaleFactor);
            if (newScale <= 1) {
              return initialState;
            }
            return { ...previous, scale: newScale };
          });
        }
        lastPinchDistanceRef.current = currentDistance;
      }
    },
    [clampScale]
  );

  const handleTouchEnd = useCallback(() => {
    lastPinchDistanceRef.current = 0;
  }, []);

  return {
    state,
    resetZoom,
    handleWheel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleDoubleClick,
    handleTouchStart,
    handleNativeTouchMove,
    handleTouchEnd,
    isZoomed: state.scale > 1,
  };
}
